/**
 * bnc-shipping-protection.js
 * Cart-drawer shipping-protection toggle for BeachNapClub (Horizon 3.0.0).
 *
 * Ported behavior from Dopamiles assets/dopamiles-cart.js
 * (wireShippingProtection / handleSPToggle / maybeAutoAddSP), rewritten
 * against Horizon's cart pipeline:
 *   - Horizon morphs the drawer in place (Idiomorph-style) on every cart
 *     change and does NOT re-run connectedCallback, so binding is done by
 *     DOCUMENT-LEVEL EVENT DELEGATION (survives every morph).
 *   - morph's updateInput() copies the SSR `checked` attribute back onto
 *     the live checkbox, ignoring the client opt-out flag. So a
 *     MutationObserver re-syncs the checkbox after each morph (timing-
 *     independent: catches both synchronous morphSection and async
 *     renderSection paths).
 *   - Drawer refresh is driven by dispatching a `cart:update` event on
 *     document (the contract cart-items-component listens for), NOT by
 *     window.dopCart / PUB_SUB.
 *
 * sessionStorage:
 *   bnc_sp_opted_out  — set when shopper removes SP; skip auto-add this session
 *   bnc_sp_auto_added — set after first auto-add; prevents re-add loop
 */
(function () {
  'use strict';

  var OPT_OUT_KEY = 'bnc_sp_opted_out';
  var AUTO_ADDED_KEY = 'bnc_sp_auto_added';
  var ROOT_SEL = '[data-bnc-shipping-protect]';
  var TOGGLE_SEL = '[data-bnc-sp-toggle]';

  var addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  var changeUrl = (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change.js';

  var inFlight = false; // serialize SP mutations; avoid auto-add / toggle races

  // ── sessionStorage (Safari Private throws on setItem) ─────────────────
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (_) {} }
  function ssRemove(k) { try { sessionStorage.removeItem(k); } catch (_) {} }

  function getCartSectionId() {
    var el = document.querySelector('cart-items-component[data-section-id]');
    return el ? el.dataset.sectionId : null;
  }

  // POST a cart mutation, requesting the drawer section so the re-render is
  // a synchronous morphSection (no second network round-trip).
  function postCart(url, payload) {
    var sectionId = getCartSectionId();
    if (sectionId) {
      payload.sections = sectionId;
      payload.sections_url = window.location.pathname;
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
    }).then(function (r) { return r.json(); });
  }

  // Tell cart-items-component to refresh. Passing `sections` makes it morph
  // synchronously; otherwise it re-fetches the section itself.
  // NOTE: cart-drawer-component also listens on this event NAME (not the
  // CartAddEvent class) and calls showDialog() if it has `auto-open`. That
  // is a no-op here because every dispatch happens while the drawer is
  // already open (toggle = user in the open drawer; auto-add = gated behind
  // the drawer-open hook) and showDialog() early-returns when open. Do NOT
  // dispatch this while the drawer is closed or it will pop open.
  function refreshDrawer(result, sourceId) {
    var detail = {
      resource: result || {},
      sourceId: sourceId || 'bnc-sp',
      data: { source: 'bnc-sp' },
    };
    if (result && result.sections) detail.data.sections = result.sections;
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: detail }));
  }

  // Keep the checkbox state honest after a morph: opt-out (and SP not in
  // cart) → unchecked; SP in cart → checked. Liquid can't read
  // sessionStorage, so it always SSRs `checked` when default_checked.
  function reconcile() {
    var root = document.querySelector(ROOT_SEL);
    if (!root) return;
    var cb = root.querySelector(TOGGLE_SEL);
    if (!cb) return;
    var inCart = root.dataset.spInCart === 'true';
    var optedOut = ssGet(OPT_OUT_KEY) === '1';
    if (inCart) cb.checked = true;
    else if (optedOut) cb.checked = false;
  }

  // ── Toggle handler (document-delegated; survives morph) ───────────────
  function onToggle(cb) {
    if (cb.disabled || inFlight) return;
    var root = cb.closest(ROOT_SEL);
    if (!root) return;
    var variantId = root.dataset.spVariantId;
    if (!variantId) return;

    var turningOn = cb.checked;
    var inCart = root.dataset.spInCart === 'true';

    // Defensive: toggle-off while SP isn't actually in cart (SSR rendered
    // checked via default_checked before auto-add fired). Record opt-out,
    // leave it unchecked, skip the doomed change call.
    if (!turningOn && !inCart) {
      ssSet(OPT_OUT_KEY, '1');
      return;
    }

    inFlight = true;
    cb.disabled = true;
    var op;
    if (turningOn) {
      ssRemove(OPT_OUT_KEY);
      op = postCart(addUrl, { id: parseInt(variantId, 10), quantity: 1 });
    } else {
      ssSet(OPT_OUT_KEY, '1');
      // /cart/change.js accepts a variant id as `id`.
      op = postCart(changeUrl, { id: String(variantId), quantity: 0 });
    }
    op.then(function (result) {
      refreshDrawer(result, 'bnc-sp-toggle');
    }).catch(function (err) {
      console.error('[bnc-sp] toggle failed', err);
      cb.checked = !turningOn; // revert UI
      if (turningOn) ssSet(OPT_OUT_KEY, '1'); else ssRemove(OPT_OUT_KEY);
    }).finally(function () {
      inFlight = false;
      if (document.contains(cb)) cb.disabled = false;
    });
  }

  // ── Auto-add once per session ─────────────────────────────────────────
  function maybeAutoAdd() {
    if (inFlight) return;
    var root = document.querySelector(ROOT_SEL);
    if (!root) return;
    var variantId = root.dataset.spVariantId;
    var defaultChecked = root.dataset.spDefaultChecked === 'true';
    if (!variantId || !defaultChecked) return;
    if (ssGet(OPT_OUT_KEY) === '1') return;
    if (ssGet(AUTO_ADDED_KEY) === '1') return;

    if (root.dataset.spInCart === 'true') {
      ssSet(AUTO_ADDED_KEY, '1'); // already present — lock, no fetch
      return;
    }

    inFlight = true;
    ssSet(AUTO_ADDED_KEY, '1'); // lock before fetch to prevent re-entry
    postCart(addUrl, { id: parseInt(variantId, 10), quantity: 1 })
      .then(function (result) { refreshDrawer(result, 'bnc-sp-auto'); })
      .catch(function (err) {
        console.error('[bnc-sp] auto-add failed', err);
        // Release the lock so a transient failure retries on the next
        // drawer open instead of silently dropping SP for the session.
        ssRemove(AUTO_ADDED_KEY);
      })
      .finally(function () { inFlight = false; });
  }

  // ── Wiring ────────────────────────────────────────────────────────────
  // Toggle: delegate on document so it survives every drawer morph.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches(TOGGLE_SEL)) onToggle(t);
  });

  // Reconcile after each morph. Observe the stable drawer host element;
  // morph mutates attributes/children inside it but never replaces it.
  function observeDrawer() {
    var host = document.querySelector('cart-drawer-component');
    if (!host) return;
    var scheduled = false;
    var obs = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; reconcile(); });
    });
    obs.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-sp-in-cart', 'checked', 'open'],
    });
  }

  // Auto-add on first drawer open per session (proven theme pattern:
  // dialog `open` attribute + cart-drawer trigger click).
  function hookDrawerOpen() {
    var dialog = document.querySelector('.cart-drawer__dialog');
    var btn = document.querySelector('.cart-drawrer-btn');
    if (btn) btn.addEventListener('click', function () { setTimeout(maybeAutoAdd, 150); });
    if (dialog) {
      new MutationObserver(function () {
        if (dialog.open) setTimeout(maybeAutoAdd, 150);
      }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  function init() {
    observeDrawer();
    hookDrawerOpen();
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
